'use client';

import * as React from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getUi, toggleSelectedMediaItems } from '@/store/reducers/ui-slice';
import { Media } from '@alien-platform/cms-bo';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CheckCircle } from '@phosphor-icons/react/dist/ssr/CheckCircle';
import { DotsThree as DotsThreeIcon } from '@phosphor-icons/react/dist/ssr/DotsThree';

import { dayjs } from '@/lib/dayjs';
import { bytesToSize } from '@/lib/helpers';
import { useMediaQuery } from '@/hooks/use-media-query';
import { usePopover } from '@/hooks/use-popover';
import { FileIcon } from '@/components/core/file-icon';
import { toast } from '@/components/core/toaster';

import { ItemMenu } from './item-menu';

export interface ItemCardProps {
  item: Media;
  onDelete?: (itemId: string) => void;
  onFavorite?: (itemId: string, value: boolean) => void;
  onOpen?: (itemId: string) => void;
  target: {
    customKey: string;
    index: number;
    item: unknown;
  };
}

export function MediaPickerItem({ item, onDelete, onFavorite, onOpen, target }: ItemCardProps): React.JSX.Element {
  const allowedFormats = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

  const popover = usePopover<HTMLButtonElement>();
  const { selectedMediaItems } = useAppSelector(getUi);
  const dispatch = useAppDispatch();
  const mdDown = useMediaQuery('down', 'md');

  const handleDelete = React.useCallback(() => {
    popover.handleClose();
    onDelete?.(item._id);
  }, [item, popover, onDelete]);

  const handleSelectMedia = (): void => {
    if (!allowedFormats.includes(item.type)) {
      toast.error('Invalid file format! Only JPG, PNG, GIF, WEBP, and SVG are allowed.');
      return;
    }
    onFavorite?.(item._id, true);
    dispatch(toggleSelectedMediaItems({ media: item, target }));
  };

  const createdAt = item.createdAt ? dayjs(item.createdAt).format('MMM D, YYYY') : undefined;

  return (
    <React.Fragment>
      <Card
        key={item._id}
        sx={{
          transition: 'box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          '&:hover': { boxShadow: 'var(--mui-shadows-16)' },
          border: `2px solid ${selectedMediaItems?.media?._id === item._id ? 'var(--mui-palette-primary-main)' : 'transparent'}`,
        }}
        onClick={handleSelectMedia}
      >
        <Stack direction="row" spacing={3} sx={{ alignItems: 'center', justifyContent: 'space-between', pt: 2, px: 2 }}>
          {/* <IconButton
            onClick={() => {
              //   onFavorite?.(item.id, !item.isFavorite);
            }}
          >
            <StarIcon color="var(--mui-palette-warning-main)" weight={item.isFavorite ? 'fill' : undefined} />
          </IconButton> */}

          <CheckCircle
            color="var(--mui-palette-warning-main)"
            fontSize={24}
            weight={selectedMediaItems?.media?._id === item._id ? 'fill' : undefined}
          />

          <IconButton onClick={popover.handleOpen} ref={popover.anchorRef}>
            <DotsThreeIcon weight="bold" />
          </IconButton>
        </Stack>
        <Stack divider={<Divider />} spacing={1} sx={{ p: 2 }}>
          <Box
            onClick={() => {
              onOpen?.(item._id);
            }}
            sx={{ display: 'inline-flex', cursor: 'pointer', width: 1 }}
          >
            <FileIcon path={item.path} extension={item.extension} />
          </Box>
          <div>
            {mdDown ? null : (
              <Typography
                onClick={() => {
                  onOpen?.(item._id);
                }}
                sx={{ cursor: 'pointer' }}
                variant="subtitle2"
              >
                {item.path}
              </Typography>
            )}

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography color="text.secondary" variant="body2">
                {bytesToSize(item.size)}
                {/* {item.type === 'folder' ? <span> • {item.itemsCount} items</span> : null} */}
              </Typography>
              <div>
                {/* {item.isPublic ? (
                  <Tooltip title="Public">
                    <Avatar sx={{ '--Avatar-size': '32px' }}>
                      <GlobeIcon fontSize="var(--Icon-fontSize)" />
                    </Avatar>
                  </Tooltip>
                ) : null} */}
              </div>
            </Stack>
            <Typography color="text.secondary" variant="caption">
              Created at {createdAt}
            </Typography>
          </div>
        </Stack>
      </Card>
      <ItemMenu
        anchorEl={popover.anchorRef.current}
        onClose={popover.handleClose}
        onDelete={handleDelete}
        open={popover.open}
        mediaId={item._id}
        path={item.path}
      />
    </React.Fragment>
  );
}
